import { BlipFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/blip-fill/blip-fill-converter';
import { GradientFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/gradient-fill/gradient-fill-converter';
import { PatternFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/pattern-fill/pattern-fill-converter';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import {
    GradientType,
    IBackgroundFillInfo,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { enumerateGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import { DeltaOf, ICTBackgroundProperties, ICTShapeProperties, ICTGroupShapeProperties } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import _set from 'lodash/set';
import _get from 'lodash/get';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';
import { IEGColorChoice } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';

export class FillConverter {
    /**
     * 数据3.0转换接口
     * @param sdkData
     * @param uiData
     */
    // eslint-disable-next-line complexity
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTShapeProperties | ICTBackgroundProperties | ICTGroupShapeProperties>>,
        uiData: IBackgroundFillInfo,
    ): void {
        if (sdkData.grpFill !== undefined && sdkData.grpFill !== null) {
            uiData.grpFill = sdkData.grpFill ? 1 : 0;
        }
        if (sdkData.solidFill) {
            // uiData.solidFill = new SolidFill('', sdkData?.solidFill?.egColorChoice?.srgbClr);
            if (sdkData.solidFill?.egColorChoice) {
                const tdColor = ColorConverter.convertPartialDataFromSdkToUi(sdkData.solidFill?.egColorChoice);
                uiData.solidFill = new SolidFill(tdColor);
            }
        }
        if (sdkData.pattFill) {
            uiData.pattFill = PatternFillConverter.convertPartialDataFromSdkToUi(sdkData.pattFill);
        }
        if (sdkData.gradFill) {
            uiData.gradFill = GradientFillConverter.convertPartialDataFromSdkToUi(sdkData.gradFill);
        }
        if (sdkData.blipFill?.blip) {
            uiData.image = BlipFillConverter.convertPartialDataFromSdkToUi(sdkData.blipFill);
        }
        if (sdkData.noFill !== undefined && sdkData.noFill !== null) {
            uiData.noFill = sdkData.noFill ? 1 : 0;
        }
        if (sdkData.grpFill !== undefined && sdkData.grpFill !== null) {
            uiData.grpFill = sdkData.grpFill ? 1 : 0;
        }
        if ('shadeToTitle' in sdkData) {
            uiData.shadeToTitle = sdkData.shadeToTitle ? 1 : 0;
        }
    }

    public static applyGrpFill(uiData: IElementBaseModel): void {
        if (uiData.props.style.fillBackground?.grpFill === 1) {
            enumerateGroup(uiData, (groupModel) => {
                if (groupModel?.props.style?.fillBackground?.grpFill !== 1) {
                    const fillBackground = deepCopy(groupModel.props.style.fillBackground);
                    if (_get(fillBackground, 'gradFill.pathGradient.path') === GradientType.Shape) {
                        fillBackground.gradFill.pathGradient.path = GradientType.Radial;
                    }
                    uiData.props.style.fillBackground = { ...fillBackground, ...uiData.props.style.fillBackground };
                    return true;
                }
                return false;
            });
        }
    }

    public static needConvertSdkData(sdkData: DeepReadonly<
        DeepPartial<ICTShapeProperties | ICTBackgroundProperties | ICTGroupShapeProperties>>): boolean {
        return !!(
            sdkData?.noFill
            || sdkData?.grpFill
            || sdkData?.blipFill
            || sdkData?.pattFill
            || sdkData?.solidFill
            || sdkData?.gradFill
        );
    }

    // eslint-disable-next-line complexity
    public static convertPartialDataFromUiToSdk(
        partialUiData: DeepPartial<IBackgroundFillInfo>,
        toSdkData: DeltaOf<ICTShapeProperties | ICTBackgroundProperties | ICTGroupShapeProperties>,
    ): void {
        // 纯色填充
        if (partialUiData.solidFill !== undefined) {
            const egColorChoice: IEGColorChoice = {};
            if (partialUiData.solidFill?.color) {
                ColorConverter.convertPartialDataFromUiToSdk(partialUiData.solidFill.color, egColorChoice);
                _set(toSdkData, 'solidFill.egColorChoice', egColorChoice);
            }
        } else {
            toSdkData.solidFill = null;
        }
        // 纯色填充 兜底逻辑 + 上报日志追源（后续定位到后需删除）
        if (partialUiData.solidFill?.color?.colorValue === 'undefined') {
            toSdkData.solidFill = null;
        }
        // 图片填充
        if (partialUiData.image) {
            toSdkData.blipFill = BlipFillConverter.convertPartialDataFromUiToSdk(partialUiData.image);
        } else {
            toSdkData.blipFill = null;
        }
        // 渐变填充
        if (partialUiData.gradFill) {
            // @ts-ignore
            toSdkData.gradFill = GradientFillConverter.convertPartialDataFromUiToSdk(partialUiData.gradFill);
        } else {
            toSdkData.gradFill = null;
        }
        // 图案填充
        if (partialUiData.pattFill) {
            toSdkData.pattFill = PatternFillConverter.convertPartialDataFromUiToSdk(partialUiData.pattFill);
        } else {
            toSdkData.pattFill = null;
        }
        // 无填充
        if (partialUiData.noFill !== undefined) {
            toSdkData.noFill = !! partialUiData.noFill;
        } else {
            toSdkData.noFill = null;
        }
        // 组合填充
        if (partialUiData.grpFill !== undefined) {
            toSdkData.grpFill = !! partialUiData.grpFill;
        } else {
            toSdkData.grpFill = null;
        }
        // 标题阴影
        if (partialUiData.shadeToTitle !== undefined) {
            (toSdkData as DeltaOf<ICTBackgroundProperties>).shadeToTitle = !!partialUiData.shadeToTitle;
        } else {
            (toSdkData as DeltaOf<ICTBackgroundProperties>).shadeToTitle = null;
        }
    }
}
